import Vue from 'vue'
import { get, set } from 'lodash'
import helpers from './helpers'

const debug = false

export default ({ ui, locale, ls, api, appCache }) => {
  // Share components
  const shared = require.context('./Components/Shared', true, /\.(vue)$/i)
  shared.keys().map(key => Vue.component(key.substr(key.lastIndexOf('/') + 1).replace('.vue', ''), shared(key).default))

  // Require components
  const required = require.context('./Components/Required', true, /\.(vue)$/i)
  required.keys().map(key => ui.component(key.substr(key.lastIndexOf('/') + 1).replace('.vue', ''), required(key).default))

  const token = helpers.getStoredToken({ debug, ls })
  api.setToken(token)

  return Vue.prototype.$account = new Vue({
    data () {
      return {
        fetched: false,
        token,
        impersonation_token: ls('sma_impersonation_token'),
        current: helpers.getCurrentAccount({ token, ls, locale, appCache, debug })
      }
    },
    watch: {
      token (value) {
        ls('sma_token', value)
        api.setToken(value)
        if (debug) console.log('[ACCOUNT] Stored and registered token', value)
      },
      impersonation_token (value) {
        ls('sma_impersonation_token', value)
      },
      current (newValue, oldValue) {
        if (newValue.id !== null) {
          ls('sma_current', newValue)
          if (debug) console.log('[ACCOUNT] Stored account infos:', JSON.stringify(newValue))

          if (newValue.id !== oldValue.id) {
            if (debug) console.log('[ACCOUNT] Dispatching account changed ...')
            this.$emit('changed', this.current)
          }
        } else {
          if (debug) console.log('[ACCOUNT] Removing stored account info to anonymous.')
          this.$emit('changed', this.current)
        }
      }
    },
    computed: {
      isAuthenticated () {
        return this.token !== null && this.current.id !== null
      },
      isVerified () {
        return this.isAuthenticated && this.current.verified_at !== null
      },
      isAdministrator () {
        return this.current.role === 'ADMINISTRATOR'
      },
      isImpersonated () {
        return this.impersonation_token !== null
      },
      initials () {
        if (this.current.id) {
          return this.current.first_name.substr(0, 1) + this.current.last_name.substr(0, 1)
        } else {
          return null
        }
      },
      color () {
        const name = this.current.first_name + ' ' + this.current.last_name
        let hash = 0
        for (let i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 5) - hash)
        }
        return helpers.HSLToHex(Math.abs(hash % 360), 50, 50)
      }
    },
    methods: {
      async authenticate ({
        token,
        email,
        password,
        code,
        _passive_client_token,
        resurrect
      } = {}, {
        onProgress = () => {}
      } = {}) {
        try {
          // First, retrieve the token if we authenticate with email + password
          if (token) {
            if (debug) console.log('[ACCOUNT] Authenticating with token', token, '...')
            this.token = token
          } else {
            if (debug) console.log('[ACCOUNT] Authenticating credentials, getting token first ...')
            onProgress('getting-token')
            const response = await api.post('account/tokens/generate', {
              email,
              password,
              code,
              _passive_client_token,
              resurrect
            })

            this.token = response.data.token
            if (debug) console.log('[ACCOUNT] Got token', this.token, '!')
          }

          // When we have a token, fetch the account
          onProgress('loading-account')
          return await this.fetch()
        } catch (err) {
          if (debug) console.log('[ACCOUNT] Authentication error', err)

          // In case of error, cleanup the account state
          this.token = null
          this.account = helpers.getAnonymousAccount({ appCache, locale })

          // Then send the error
          throw err
        }
      },
      async logout () {
        if (this.token !== null) {
          this.token = null
          this.current = helpers.getAnonymousAccount({ appCache, locale })
        }
      },
      async fetch (options = {}) {
        // We need to wait a bit because we usually change the token just before fetch(), so it may run to soon
        // For example, the token may not be changed to axios because of the token watch trigger is too late.
        await Vue.nextTick()

        const response = await api.get('account', { with_customer: true }, options)

        if (this.token) {
          if (debug) console.log('[ACCOUNT] Fetched account informations', JSON.stringify(response.data))
          this.fetched = true
          this.current = response.data
        } else {
          if (debug) console.log('[ACCOUNT] Could not store fetched informations, token was removed before ending.')
        }

        await Vue.nextTick()

        return this.current
      },
      async update (attributes) {
        if (!this.current.id) {
          throw 'Could not save attributes into an anonymous user.'
        } else {
          await this.$recaptchaLoaded()
          await api.put('account', {
            _passive_client_token: await this.$recaptcha('account_update'),
            ...attributes
          })
          return await this.fetch()
        }
      },

      getSubscriptions () {
        if (this.current.customer) {
          return this.current.customer.subscriptions
        } else {
          return {}
        }
      },
      getSubscription (module) {
        if (this.current.customer) {
          if (module in this.current.customer.subscriptions) {
            return this.current.customer.subscriptions['check-position']
          }
        }

        return { name: 'default', options: [] }
      },

      getMeta (name, missing) {
        return get(this.current.meta, name, missing)
      },
      async setMeta (name, value) {
        set(this.current.meta, name, value)

        // The previous set may not trigger the watch as the value may be nested far far away,
        // so we have call it manually, it will make the whole app reactive.
        this.current.__ob__.dep.notify()

        await this.$recaptchaLoaded()
        await api.put('account', {
          _passive_client_token: await this.$recaptcha('account_update'),
          meta: {
            [name]: value
          }
        })
      },

      async impersonate (user) {
        this.impersonation_token = this.token
        const response = await api.post('users/' + user.id + '/tokens/generate')
        this.token = response.data.token
        return await this.fetch()
      },
      async depersonate () {
        this.token = this.impersonation_token
        const account = await this.fetch()
        this.impersonation_token = null
        return account
      },

      on (event, callback) {
        return this.$on(event, callback)
      },
      once (event, callback) {
        return this.$once(event, callback)
      },
      off (event, callback) {
        return this.$off(event, callback)
      },
      emit (event, payload) {
        return this.$emit(event, payload)
      },

      async routerBeforeEach (to, from, next) {
        const allowedRoutes = ['login', 'forgot_password']

        if (debug) console.log('[ACCOUNT] Guard: navigating from ' + from.path + ' (' + from.name + ') to ' + to.path + ' (' + to.name + ') ...')

        if (this.isAuthenticated) {
          if (debug) console.log('[ACCOUNT] Guard: user is authenticated, trying to refresh user with token ...')

          // If we are authenticated and we don't have fetched the account yet, rely on
          // localstorage user first, and fetch api user in background for verification
          if (!this.fetched) this.fetch()

          next()
        } else {
          // If the user is moving to an allowed route, continue ...
          if (to.name && allowedRoutes.includes(to.name)) {
            if (debug) console.log('[ACCOUNT] Guard: user is not authenticated but on allowed route, skipping ...')
            next()
          } else {
            if (from.name === 'login') {
              if (debug) console.log('[ACCOUNT] Guard: user is not authenticated and already on login page, abort navigation')
              next(false)
            } else {
              if (debug) console.log('[ACCOUNT] Guard: user is not authenticated, moving from ' + from.path + ' to /login')
              next('/login')
            }
          }
        }
      }
    }
  })
}